// TESSERAX — RAIL DE RYTHME (« pace ») : cœur pur, testé.
//
// Le défi du jour est DÉTERMINISTE : à graine égale, la séquence de pièces est
// identique d'une partie à l'autre. On peut donc faire courir le joueur contre sa
// PROPRE meilleure tentative sur CETTE graine — « suis-je en train de battre
// moi-d'avant, là, maintenant ? ». Pas de second puits à l'écran (lisibilité
// intacte) : un seul scalaire qui respire (écart au rival à l'instant courant).
//
// Une TRAJECTOIRE = suite de points [tMs, score] enregistrés quand le score MONTE
// (donc croissante en temps ET en score). Le score d'un rival à un instant est une
// fonction en ESCALIER : ce qu'il avait ATTEINT à cet instant (on n'invente pas de
// points « à moitié gagnés »). Une fois sa partie finie, son score FINAL tient — le
// joueur peut donc le rattraper puis le dépasser en fin de partie.
//
// Pur : aucune dépendance THREE / DOM / localStorage / RNG. L'enregistrement et le
// rendu (DOM) vivent dans main.ts ; ce module ne porte que la logique de données,
// testable unitairement. N'altère ni le tirage ni le score → déterminisme intact.

// Un point de trajectoire : [temps écoulé en ms depuis le début de la partie, score atteint].
export type PaceSample = [number, number];

// Recherche dichotomique du DERNIER point dont le temps est ≤ tMs (−1 si aucun).
// Trajectoire supposée triée par temps croissant (invariant garanti par l'enregistrement).
function lastIndexAt(traj: PaceSample[], tMs: number): number {
  let lo = 0, hi = traj.length - 1, res = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (traj[mid][0] <= tMs) { res = mid; lo = mid + 1; }
    else hi = mid - 1;
  }
  return res;
}

// Score du rival à l'instant écoulé tMs (fonction en escalier). Avant son 1er point → 0 ;
// après son dernier → son score FINAL (sa partie est finie, le total tient).
export function scoreAt(traj: PaceSample[], tMs: number): number {
  if (!traj || traj.length === 0) return 0;
  const i = lastIndexAt(traj, tMs);
  return i < 0 ? 0 : traj[i][1];
}

// Faut-il POSER un point ? On échantillonne quand le score a STRICTEMENT augmenté et
// qu'au moins `minGapMs` s'est écoulé depuis le dernier point — trajectoire compacte
// (un point par palier de score, throttlé) sans perdre la forme. Pour une trajectoire
// vide, l'appelant passe lastScore=0 et lastT très négatif (le 1er score qualifie).
export function shouldSample(lastT: number, lastScore: number, tMs: number, score: number, minGapMs: number): boolean {
  return score > lastScore && (tMs - lastT) >= minGapMs;
}

// Borne la taille d'une trajectoire avant stockage (quota localStorage) : si elle dépasse
// `max` points, on la sous-échantillonne régulièrement en CONSERVANT le premier et le
// dernier (les extrémités portent le départ et le score final). Idempotente sous le seuil.
export function compactTrajectory(traj: PaceSample[], max: number): PaceSample[] {
  if (!traj || traj.length <= max || max < 2) return traj && traj.length > max ? traj.slice(-max) : traj;
  const step = Math.ceil(traj.length / max);
  const out: PaceSample[] = [];
  for (let i = 0; i < traj.length; i += step) out.push(traj[i]);
  const last = traj[traj.length - 1];
  if (out.length === 0 || out[out.length - 1] !== last) out.push(last);
  return out;
}

// Écart de rythme à l'instant tMs : score du rival alors, et delta = mon score − le sien.
// delta > 0 → en avance (▲ vert) ; < 0 → en retard (▼ rouge) ; 0 → au coude-à-coude.
export interface PaceDelta { rival: number; delta: number; }
export function paceDelta(traj: PaceSample[], tMs: number, myScore: number): PaceDelta {
  const rival = scoreAt(traj, tMs);
  return { rival, delta: myScore - rival };
}
